import { supabase } from '@/lib/supabaseClient';
import { notificationService } from './notificationService';
import { bookingReminderService } from './bookingReminderService';
import { emailService } from './emailService';

interface ProjectWithSalesRep {
  id: string;
  project_name: string;
  assigned_sales_rep_id: string;
  sales_rep: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  } | null;
}

// Helper function to get ordinal suffix for dates
function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

interface BookingNotificationData {
  bookingId: string;
  projectId?: string;
  customerName: string;
  customerEmail: string;
  salesRepName: string;
  bookingDate: string;
  bookingTime: string;
  address: string;
  eventName: string;
  eventType: string;
  customerTimezone?: string;
  metadata?: {
    phoneNumber?: string;
    [key: string]: any;
  };
}

export const bookingNotificationService = {
  async notifyBookingCreated(bookingData: BookingNotificationData): Promise<{ emailSent: boolean; emailError?: string }> {
    try {
      console.log('[bookingNotificationService] Starting booking notification:', {
        bookingId: bookingData.bookingId,
        projectId: bookingData.projectId,
        customerName: bookingData.customerName
      });

      // Get the current user (who created the booking)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('[bookingNotificationService] No authenticated user found');
        return { emailSent: false, emailError: 'No authenticated user found' };
      }

      // Format the booking date and time with timezone
      // Handle different time formats (e.g., "2:00 PM", "14:00", "TBD")
      let bookingDateTime: Date;

      if (bookingData.bookingTime === 'TBD' || !bookingData.bookingTime) {
        // For TBD or missing time, use a default time (9:00 AM)
        bookingDateTime = new Date(`${bookingData.bookingDate}T09:00:00`);
      } else if (bookingData.bookingTime.includes('AM') || bookingData.bookingTime.includes('PM')) {
        // Parse 12-hour format (e.g., "2:00 PM")
        const [time, period] = bookingData.bookingTime.split(' ');
        const [hours, minutes] = time.split(':').map(Number);
        let hour24 = hours;

        if (period === 'PM' && hours !== 12) {
          hour24 += 12;
        } else if (period === 'AM' && hours === 12) {
          hour24 = 0;
        }

        bookingDateTime = new Date(`${bookingData.bookingDate}T${hour24.toString().padStart(2, '0')}:${(minutes || 0).toString().padStart(2, '0')}:00`);
      } else {
        // Assume 24-hour format or already properly formatted
        const timeStr = bookingData.bookingTime.includes(':') ? bookingData.bookingTime : `${bookingData.bookingTime}:00`;
        bookingDateTime = new Date(`${bookingData.bookingDate}T${timeStr}`);
      }

      console.log('[bookingNotificationService] Parsed booking date/time:', {
        originalDate: bookingData.bookingDate,
        originalTime: bookingData.bookingTime,
        parsedDateTime: bookingDateTime.toISOString(),
        isValid: !isNaN(bookingDateTime.getTime())
      });

      // Validate the parsed date
      if (isNaN(bookingDateTime.getTime())) {
        console.error('[bookingNotificationService] Invalid date/time parsed, using fallback', {
          bookingDate: bookingData.bookingDate,
          bookingTime: bookingData.bookingTime
        });
        // Fallback to today + 1 day at 9 AM
        bookingDateTime = new Date();
        bookingDateTime.setDate(bookingDateTime.getDate() + 1);
        bookingDateTime.setHours(9, 0, 0, 0);
      }

      // Get timezone information - try to use customer timezone if available
      let timezoneAbbr = '';
      let timezoneInfo = '';
      
      // Check if we have customer timezone information
      if (bookingData.customerTimezone) {
        timezoneAbbr = new Intl.DateTimeFormat('en-US', {
          timeZone: bookingData.customerTimezone,
          timeZoneName: 'short'
        }).formatToParts(bookingDateTime).find(part => part.type === 'timeZoneName')?.value || '';
        timezoneInfo = ` (${timezoneAbbr})`;
      } else {
        // Fallback to user's timezone
        timezoneAbbr = new Intl.DateTimeFormat('en-US', {
          timeZoneName: 'short'
        }).formatToParts(bookingDateTime).find(part => part.type === 'timeZoneName')?.value || '';
        timezoneInfo = ` (${timezoneAbbr})`;
      }
      
      // Format date with ordinal day
      const day = bookingDateTime.getDate();
      const ordinalSuffix = getOrdinalSuffix(day);
      const formattedDate = bookingDateTime.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric'
      }).replace(/\d+$/, day + ordinalSuffix).replace(',', '');
      
      // Format time with AM/PM
      const formattedTime = bookingDateTime.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      // Combine date, time, and timezone
      const formattedDateTime = `${formattedDate} at ${formattedTime}${timezoneInfo}`;

      // Skip old notification system since we have issues with UUID validation
      // Only use project team notifications (these work correctly)
      if (bookingData.projectId) {
        await this.notifyProjectTeam(bookingData, user, formattedDateTime);
      }

      // 5. Send email confirmation to sales rep using emailService
      const emailResult = await this.sendSalesRepEmailConfirmation(bookingData, formattedDateTime);

      // 6. Update project stage after site survey booking
      if (bookingData.eventType === 'site-survey') {
        try {
          // Get the current stage of the project
          const { data: project, error: fetchError } = await supabase
            .from('projects')
            .select('current_stage_id')
            .eq('id', bookingData.projectId)
            .single();

          if (fetchError) {
            console.error('[bookingNotificationService] Error fetching project stage:', fetchError);
          } else if (project) {
            let newStageId: number | null = null;

            // Determine the new stage based on current stage
            if (project.current_stage_id === 642) {
              newStageId = 643;
            } else if (project.current_stage_id === 49) {
              newStageId = 50;
            }

            // Update the stage if we have a new stage to transition to
            if (newStageId) {
              const { error: updateError } = await supabase
                .from('projects')
                .update({
                  current_stage_id: newStageId,
                  updated_at: new Date().toISOString()
                })
                .eq('id', bookingData.projectId);

              if (updateError) {
                console.error('[bookingNotificationService] Error updating project stage:', updateError);
              } else {
                console.log('[bookingNotificationService] Project stage updated successfully:', {
                  projectId: bookingData.projectId,
                  oldStageId: project.current_stage_id,
                  newStageId: newStageId
                });
              }
            } else {
              console.log('[bookingNotificationService] No stage update needed for current stage:', project.current_stage_id);
            }
          }
        } catch (stageError) {
          console.error('[bookingNotificationService] Error in stage update process:', stageError);
          // Don't fail the booking if stage update fails
        }
      }

      // 7. Schedule reminders for the booking
      try {
        // Get the booking details to schedule reminders
        const { data: booking } = await supabase
          .from('bookings')
          .select(`
            *,
            event:events (
              id,
              name,
              user_id,
              calendar_id
            )
          `)
          .eq('id', bookingData.bookingId)
          .single();

        if (booking) {
          await bookingReminderService.scheduleBookingReminders(booking);
        }
      } catch (reminderError) {
        console.error('[bookingNotificationService] Error scheduling reminders:', reminderError);
        // Don't fail the booking if reminders fail
      }

      console.log('[bookingNotificationService] Booking notifications sent successfully');
      return emailResult;
    } catch (error) {
      console.error('[bookingNotificationService] Error sending booking notifications:', error);
      return { emailSent: false, emailError: 'Failed to send notifications' };
    }
  },

  async notifySalesRep(bookingData: BookingNotificationData, currentUser: any, formattedDateTime: string): Promise<void> {
    try {
      // NOTE: Only salesRepName is available; ideally resolve to user ID if needed
      await notificationService.createNotification({
        user_id: bookingData.salesRepName, // Use name as fallback; ideally resolve to user ID
        type: 'booking_scheduled_host',
        title: 'Booking Scheduled',
        message: 'A new booking has been scheduled.',
        metadata: {
          booking_id: bookingData.bookingId,
          project_id: bookingData.projectId,
          customer_name: bookingData.customerName,
          customer_email: bookingData.customerEmail,
          booking_date: formattedDateTime,
          booking_time: formattedDateTime,
          address: bookingData.address,
          event_name: bookingData.eventName,
          event_type: bookingData.eventType,
          calendar_url: `https://go.solerro.com/app/scheduler/appointments?showAllBookings=true&assignedUser=${bookingData.salesRepName}&bookingId=${bookingData.bookingId}`
        },
        project_id: bookingData.projectId,
        sender_id: currentUser.id,
        sender_name: `${currentUser.user_metadata?.first_name || ''} ${currentUser.user_metadata?.last_name || ''}`.trim() || currentUser.email,
        project_name: bookingData.projectId ? 'Site Survey Booking' : undefined,
        link: `/app/scheduler/appointments?showAllBookings=true&assignedUser=${bookingData.salesRepName}&bookingId=${bookingData.bookingId}`
      });
    } catch (error) {
      console.error('[bookingNotificationService] Error notifying sales rep:', error);
    }
  },

  async notifyProjectTeam(bookingData: BookingNotificationData, currentUser: any, formattedDateTime: string): Promise<void> {
    try {
      // Get project details
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select(`
          id,
          project_name,
          assigned_sales_rep_id,
          assigned_internal_pm_id,
          assigned_installer_pm_id,
          assigned_biz_dev_rep_id
        `)
        .eq('id', bookingData.projectId)
        .single();

      if (projectError || !project) {
        console.log('[bookingNotificationService] Project not found:', bookingData.projectId);
        return;
      }

      // Get all team members who should be notified
      const teamMemberIds = [
        project.assigned_sales_rep_id,
        project.assigned_internal_pm_id,
        project.assigned_installer_pm_id,
        project.assigned_biz_dev_rep_id
      ].filter(id => id && id !== currentUser.id); // Exclude current user

      // Send notifications to each team member
      for (const memberId of teamMemberIds) {
        try {
          await notificationService.createNotification({
            user_id: memberId,
            type: 'booking_scheduled_host',
            title: 'Booking Scheduled',
            message: 'A new booking has been scheduled.',
            metadata: {
              booking_id: bookingData.bookingId,
              project_id: bookingData.projectId,
              project_name: project.project_name,
              customer_name: bookingData.customerName,
              customer_email: bookingData.customerEmail,
              booking_date: formattedDateTime,
              booking_time: formattedDateTime,
              address: bookingData.address,
              event_name: bookingData.eventName,
              event_type: bookingData.eventType,
              calendar_url: `https://go.solerro.com/app/scheduler/appointments?showAllBookings=true&assignedUser=${memberId}&bookingId=${bookingData.bookingId}`
            },
            project_id: bookingData.projectId,
            sender_id: currentUser.id,
            sender_name: `${currentUser.user_metadata?.first_name || ''} ${currentUser.user_metadata?.last_name || ''}`.trim() || currentUser.email,
            project_name: project.project_name,
            link: `/app/scheduler/appointments?showAllBookings=true&assignedUser=${memberId}&bookingId=${bookingData.bookingId}`
          });

          console.log('[bookingNotificationService] Project team notification sent:', memberId);
        } catch (error) {
          console.error('[bookingNotificationService] Error notifying project team member:', memberId, error);
        }
      }
    } catch (error) {
      console.error('[bookingNotificationService] Error notifying project team:', error);
    }
  },

  async notifyEventOwner(bookingData: BookingNotificationData, currentUser: any, formattedDateTime: string): Promise<void> {
    try {
      // NOTE: Only eventName is available; ideally resolve to event owner user ID if needed
      await notificationService.createNotification({
        user_id: bookingData.eventName, // Use eventName as fallback; ideally resolve to event owner user ID
        type: 'booking_scheduled_host',
        title: 'Booking Scheduled',
        message: 'A new booking has been scheduled.',
        metadata: {
          booking_id: bookingData.bookingId,
          project_id: bookingData.projectId,
          customer_name: bookingData.customerName,
          customer_email: bookingData.customerEmail,
          booking_date: formattedDateTime,
          booking_time: formattedDateTime,
          address: bookingData.address,
          event_name: bookingData.eventName,
          event_type: bookingData.eventType,
          calendar_url: `https://go.solerro.com/app/scheduler/appointments?showAllBookings=true&assignedUser=${bookingData.eventName}&bookingId=${bookingData.bookingId}`
        },
        project_id: bookingData.projectId,
        sender_id: currentUser.id,
        sender_name: `${currentUser.user_metadata?.first_name || ''} ${currentUser.user_metadata?.last_name || ''}`.trim() || currentUser.email,
        link: `/app/scheduler/appointments?showAllBookings=true&assignedUser=${bookingData.eventName}&bookingId=${bookingData.bookingId}`
      });
    } catch (error) {
      console.error('[bookingNotificationService] Error notifying event owner:', error);
    }
  },

  async notifyBookingUser(bookingData: BookingNotificationData, currentUser: any, formattedDateTime: string): Promise<void> {
    try {
      // NOTE: Only customerEmail is available; ideally resolve to user ID if needed
      await notificationService.createNotification({
        user_id: bookingData.customerEmail, // Use email as fallback; ideally resolve to user ID
        type: 'booking_scheduled_attendee',
        title: 'Booking Confirmed',
        message: 'Your booking has been confirmed.',
        metadata: {
          booking_id: bookingData.bookingId,
          project_id: bookingData.projectId,
          customer_name: bookingData.customerName,
          customer_email: bookingData.customerEmail,
          booking_date: formattedDateTime,
          booking_time: formattedDateTime,
          address: bookingData.address,
          event_name: bookingData.eventName,
          event_type: bookingData.eventType,
          is_confirmation: true
        },
        project_id: bookingData.projectId,
        sender_id: currentUser.id,
        sender_name: `${currentUser.user_metadata?.first_name || ''} ${currentUser.user_metadata?.last_name || ''}`.trim() || currentUser.email,
        link: `/app/scheduler/appointments?showAllBookings=true&assignedUser=${bookingData.customerEmail}&bookingId=${bookingData.bookingId}`
      });
    } catch (error) {
      console.error('[bookingNotificationService] Error notifying booking user:', error);
    }
  },

  async sendSalesRepEmailConfirmation(bookingData: BookingNotificationData, formattedDateTime: string): Promise<{ emailSent: boolean; emailError?: string }> {
    try {
      console.log('[bookingNotificationService] Starting email confirmation to sales rep', {
        bookingId: bookingData.bookingId,
        projectId: bookingData.projectId,
        customerName: bookingData.customerName,
        eventType: bookingData.eventType
      });

      // Get the project and assigned sales rep email
      if (!bookingData.projectId) {
        console.warn('[bookingNotificationService] No project ID provided - cannot get sales rep email', {
          bookingData: bookingData
        });
        return { emailSent: false, emailError: 'No project ID provided' };
      }

      console.log('[bookingNotificationService] Fetching project and sales rep data for project:', bookingData.projectId);

      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select(`
          id,
          project_name,
          assigned_sales_rep_id,
          sales_rep:users!assigned_sales_rep_id (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .eq('id', bookingData.projectId)
        .single() as { data: ProjectWithSalesRep | null, error: any };

      console.log('[bookingNotificationService] Project query result:', {
        project: project,
        error: projectError,
        hasSalesRep: !!project?.sales_rep,
        salesRepEmail: project?.sales_rep?.email
      });

      if (projectError || !project) {
        console.error('[bookingNotificationService] Error fetching project or project not found:', {
          projectId: bookingData.projectId,
          error: projectError,
          project: project
        });
        return { emailSent: false, emailError: 'Project not found' };
      }

      if (!project.sales_rep?.email) {
        console.warn('[bookingNotificationService] No sales rep or email found for project:', {
          projectId: bookingData.projectId,
          projectName: project.project_name,
          assignedSalesRepId: project.assigned_sales_rep_id,
          salesRep: project.sales_rep
        });
        return { emailSent: false, emailError: 'No sales rep email found' };
      }

      // Prepare email data
      const subject = `Site Survey Booking Confirmation - ${bookingData.customerName}`;
      const message = `
A new site survey has been scheduled for your project: ${project.project_name || 'Untitled Project'}

**Appointment Details:**
- **Date & Time:** ${formattedDateTime}
- **Customer:** ${bookingData.customerName}
- **Email:** ${bookingData.customerEmail}
- **Phone:** ${bookingData.metadata?.phoneNumber || 'Not provided'}
- **Address:** ${bookingData.address}
- **Event Type:** ${bookingData.eventType}

Please ensure you are prepared for this appointment and reach out to the customer if needed.
      `.trim();

      console.log('[bookingNotificationService] Preparing email payload', {
        to: project.sales_rep.email,
        subject: subject,
        salesRepName: `${project.sales_rep.first_name || ''} ${project.sales_rep.last_name || ''}`.trim(),
        customerPhone: bookingData.metadata?.phoneNumber
      });

      // Get tenant_id from the project for the email service
      let tenantId = null;
      try {
        const { data: projectWithTenant, error: tenantError } = await supabase
          .from('projects')
          .select('tenant_id')
          .eq('id', bookingData.projectId)
          .single();

        console.log('[bookingNotificationService] Tenant query result:', {
          projectId: bookingData.projectId,
          tenantData: projectWithTenant,
          tenantError: tenantError,
          tenantId: projectWithTenant?.tenant_id
        });

        tenantId = projectWithTenant?.tenant_id;
      } catch (error) {
        console.warn('[bookingNotificationService] Could not fetch tenant_id from project:', error);
      }

      // For site survey bookings, use the edge function directly
      console.log('[bookingNotificationService] Using edge function for site survey booking email');

      // Create HTML email content
      const htmlContent = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 5px;">
          <h2 style="color: #333; margin-bottom: 20px;">${subject}</h2>
          <div style="color: #666; line-height: 1.6; margin-bottom: 20px;">
            <p>A new site survey has been scheduled for your project: <strong>${project.project_name || 'Untitled Project'}</strong></p>

            <div style="margin: 20px 0; padding: 15px; background-color: #f8f9fa; border-radius: 4px;">
              <h3 style="color: #333; margin-top: 0;">Appointment Details:</h3>
              <ul style="list-style: none; padding: 0;">
                <li style="margin: 8px 0;"><strong>Date & Time:</strong> ${formattedDateTime}</li>
                <li style="margin: 8px 0;"><strong>Customer:</strong> ${bookingData.customerName}</li>
                <li style="margin: 8px 0;"><strong>Email:</strong> ${bookingData.customerEmail}</li>
                <li style="margin: 8px 0;"><strong>Phone:</strong> ${bookingData.metadata?.phoneNumber || 'Not provided'}</li>
                <li style="margin: 8px 0;"><strong>Address:</strong> ${bookingData.address}</li>
                <li style="margin: 8px 0;"><strong>Event Type:</strong> ${bookingData.eventType}</li>
              </ul>
            </div>

            <p>Please ensure you are prepared for this appointment and reach out to the customer if needed.</p>
          </div>

          <div style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e0e0e0; color: #999; font-size: 12px;">
            This is an automated notification from Solerro PM.
          </div>
        </div>
      `;

      console.log('[bookingNotificationService] Calling edge function send-email with payload:', {
        to: project.sales_rep.email,
        subject: subject,
        tenant_id: tenantId,
        timestamp: new Date().toISOString()
      });

      // Call the edge function directly (Supabase client handles auth automatically)
      const emailResult = await supabase.functions.invoke('send-email', {
        body: {
          to: project.sales_rep.email,
          subject: subject,
          html: htmlContent,
          text: message,
          tenant_id: tenantId
        }
      });

      console.log('[bookingNotificationService] Edge function response:', {
        data: emailResult.data,
        error: emailResult.error,
        timestamp: new Date().toISOString()
      });

      // Check for success from edge function
      if (!emailResult.error && emailResult.data?.success === true) {
        console.log('[bookingNotificationService] ✅ Email sent successfully to sales rep:', {
          recipientEmail: project.sales_rep.email,
          recipientName: `${project.sales_rep.first_name || ''} ${project.sales_rep.last_name || ''}`.trim(),
          customerName: bookingData.customerName,
          projectName: project.project_name,
          bookingTime: formattedDateTime
        });
        return { emailSent: true };
      } else {
        const errorMessage = emailResult.error?.message || emailResult.data?.message || 'Unknown error';
        console.warn('[bookingNotificationService] ⚠️ Email sending failed:', {
          recipientEmail: project.sales_rep.email,
          error: emailResult.error,
          data: emailResult.data,
          reason: errorMessage
        });
        return {
          emailSent: false,
          emailError: errorMessage
        };
      }

    } catch (error: any) {
      console.error('[bookingNotificationService] ❌ Error sending sales rep email confirmation:', {
        error: error,
        errorMessage: error?.message,
        errorStack: error?.stack,
        bookingId: bookingData.bookingId,
        projectId: bookingData.projectId,
        customerName: bookingData.customerName,
        timestamp: new Date().toISOString()
      });
      return {
        emailSent: false,
        emailError: error?.message || 'Unknown error occurred'
      };
    }
  }
}; 